import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { approvePlatformOrderSubmission, rejectPlatformOrderSubmission, claimTask } from '../services/api';
import { showSuccess, showError } from '../components/ToastNotifications';
import {
  approveSubmissionConfirmText, approveSubmissionDoneText, askRejectReason,
} from '../utils/platformApproval';
import { resolveTaskTx, displayTaskTitle, prRecordedName } from '../utils/paymentTask';

// The task-card actions the pending-tasks pool (PendingTaskList: the dashboard
// pop-up and Task Tracking's Unclaimed tab) and Task Tracking's own cards BOTH
// offer, written once (audit D-17: the two files carried copies of every
// handler, and "the pool card and the claimed card can never disagree about
// what happens on click" was held together by a comment).
//
//   refresh        — called after any action that changed server state, and
//                    after a FAILED one too: a 409 almost always means the card
//                    was a stale snapshot (Phase 6 rule), so refetch.
//   afterNavigate  — called after a handler navigates away (the pool pop-up
//                    closes itself; Task Tracking is a page and needs nothing).
//
// Returns:
//   decidingTaskId           — the approval task whose decision is in flight
//   handleApproveSubmission  — on-behalf submission approval (task_type
//                              platform_order_approval; born claimed to the
//                              account handler, so only a decision exists)
//   handleRejectSubmission
//   handleTxAction           — deep-link into Transaction History and auto-open
//                              the action modal for this task's transaction. No
//                              task id rides along: actioning the transaction is
//                              what resolves the task, and the server derives
//                              that (PR_NOTIFICATION_NOT_HIDDEN_BY_ACTION) so it
//                              un-resolves on revert too.
//   handleAssignToCustomer   — open the assign-to-customer picker pre-selected
//                              on this task's transaction (bank / manual crypto
//                              / blockchain — utils/paymentTask resolveTaskTx).
export default function useTaskActions({ refresh, afterNavigate } = {}) {
  const navigate = useNavigate();
  const [decidingTaskId, setDecidingTaskId] = useState(null);
  const changed = () => { if (refresh) refresh(); };
  const navigated = () => { if (afterNavigate) afterNavigate(); };

  const handleApproveSubmission = async (task) => {
    if (!window.confirm(approveSubmissionConfirmText(task.platform_order_status === 'paid'))) return;
    setDecidingTaskId(task.id);
    try {
      const resp = await approvePlatformOrderSubmission(task.platform_order_id);
      showSuccess(approveSubmissionDoneText(resp));
      changed();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to approve submission');
      // Stale snapshot (already decided elsewhere, or the account changed
      // hands) → refetch so the card reflects reality instead of still
      // offering a decision it can no longer make (Phase 6 rule).
      changed();
    } finally {
      setDecidingTaskId(null);
    }
  };

  const handleRejectSubmission = async (task) => {
    const reason = askRejectReason();
    if (reason === null) return; // cancelled — no request sent
    setDecidingTaskId(task.id);
    try {
      await rejectPlatformOrderSubmission(task.platform_order_id, reason);
      showSuccess('Submission rejected.');
      changed();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to reject submission');
      changed();
    } finally {
      setDecidingTaskId(null);
    }
  };

  const handleTxAction = (task, autoAction) => {
    const tx = resolveTaskTx(task);
    if (!tx) { showError('This task has no linked transaction to action.'); return; }
    navigate('/transaction-history', { state: { focusTransaction: tx, autoAction } });
    navigated();
  };

  const handleAssignToCustomer = (task) => {
    // A task with no linked transaction at all still opens the picker on the
    // bank-money source (the id is then null — the picker shows its own empty state).
    const tx = resolveTaskTx(task) || { id: task.transaction_id, source: 'money' };
    navigate('/dashboard', {
      state: {
        openAssignTransactions: true,
        preSelectedTransaction: {
          ...tx,
          amount: task.amount,
          description: task.description || task.title,
          // No task id: the assignment itself resolves the task, server-derived.
        },
      },
    });
    navigated();
  };

  // ---- NOTICES (pr_notification, operator 2026-10-12) ----
  // Claim: whoever knows whose payment it is — sales or finance — takes it; the
  // card then shows "Claimed by you" and the actions, everybody else's pool
  // drops it. Acting on an UNCLAIMED notice (create order, assign, classify,
  // swap) claims it server-side in the same step, so these are the only two
  // claim-shaped calls the cards need. A 409 means somebody else got there
  // first — refetch so the card shows who.
  const [claimingNoticeId, setClaimingNoticeId] = useState(null);
  const handleClaimNotice = async (task) => {
    setClaimingNoticeId(task.id);
    try {
      await claimTask(task.id);
      showSuccess('Notice claimed — it is yours to link, assign or classify. Release it if it is not.');
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to claim the notice');
    } finally {
      setClaimingNoticeId(null);
      changed();
    }
  };

  // Create Order pre-filled from the notice's credit. Forwards all three
  // flavours of source row so CreateOrder can pick the right pre-fill path:
  //   - bank credit       → transaction_id (amount = MYR)
  //   - blockchain crypto → blockchain_transaction_id (+ tx_hash / token / direction)
  //   - manual crypto     → crypto_transaction_id (+ token / direction)
  // A crypto notice's amount is in the TOKEN, never MYR (an old build dumped
  // "Crypto Sent: 126642.71 USDT" into the MYR field).
  const handleCreateOrderFromNotice = (task) => {
    navigate('/create-order', {
      state: {
        pendingTask: {
          id: task.id,
          transaction_id: task.transaction_id,
          blockchain_transaction_id: task.blockchain_transaction_id,
          blockchain_tx_hash: task.blockchain_tx_hash,
          blockchain_token_symbol: task.blockchain_token_symbol,
          blockchain_direction: task.blockchain_direction, // 'incoming' | 'outgoing'
          crypto_transaction_id: task.crypto_transaction_id,
          crypto_tx_crypto_type: task.crypto_tx_crypto_type,
          crypto_tx_direction: task.crypto_tx_direction, // 'credit' | 'debit'
          amount: task.amount,
          title: displayTaskTitle(task),
          // The LIVE source-row text, not the frozen copy — an order created
          // off this notice should carry the payer name the transaction shows.
          description: prRecordedName(task) || task.description,
        },
      },
    });
    navigated();
  };

  // Create Platform Order (P2P Sell) from a BANK credit notice — the sell form
  // links the credit (link-received-credit, which applies the same notice rule).
  const handleCreatePlatformOrderFromNotice = (task) => {
    navigate('/platform', {
      state: {
        pendingTask: {
          id: task.id,
          transaction_id: task.transaction_id,
          amount: task.amount,
          // LIVE text, not the frozen copy — the platform order created off
          // this notice writes it into its description permanently.
          title: displayTaskTitle(task),
          description: prRecordedName(task) || task.description,
        },
      },
    });
    navigated();
  };

  return {
    decidingTaskId, handleApproveSubmission, handleRejectSubmission, handleTxAction, handleAssignToCustomer,
    claimingNoticeId, handleClaimNotice, handleCreateOrderFromNotice, handleCreatePlatformOrderFromNotice,
  };
}
